import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { ADMIN_MESSAGE_MAX, FEEDBACK_STATUS } from '../constants/feedback.constants';

export type FeedbackStatus = (typeof FEEDBACK_STATUS)[number];

export const feedbackUserSchema = z.object({
  id: z.coerce.number(),
  fullName: z.string(),
  username: z.string(),
  hrEmpId: z.string(),
  mobile: z.string(),
  thana: z.string().nullable(),
});

export const feedbackTypeSummarySchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  code: z.string(),
});

export const feedbackListItemSchema = z.object({
  id: z.coerce.number(),
  userId: z.coerce.number(),
  user: feedbackUserSchema.nullable(),
  typeId: z.coerce.number(),
  type: feedbackTypeSummarySchema.nullable(),
  message: z.string(),
  adminMessage: z.string().nullable(),
  status: z.enum(FEEDBACK_STATUS),
  feedbackDate: z.coerce.date(),
  readAt: z.coerce.date().nullable(),
  readBy: z.coerce.number().nullable(),
  completedAt: z.coerce.date().nullable(),
  completedBy: z.coerce.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const paginatedFeedbacksSchema = z.object({
  data: z.array(feedbackListItemSchema),
  meta: paginationMetaSchema,
});

export interface FeedbackListParams {
  startDate?: string | undefined;
  endDate?: string | undefined;
  typeId?: number | undefined;
  search?: string | undefined;
  status?: FeedbackStatus | undefined;
}

export const RESOLVE_STATUS = ['in_progress', 'completed', 'rejected'] as const;
export type ResolveStatus = (typeof RESOLVE_STATUS)[number];

export const resolveFeedbackSchema = z.object({
  status: z.enum(RESOLVE_STATUS, { error: 'Status is required' }),
  adminMessage: z
    .string()
    .trim()
    .max(ADMIN_MESSAGE_MAX, `Admin message must be ${ADMIN_MESSAGE_MAX} characters or fewer`)
    .optional(),
});

export type ResolveFeedbackPayload = z.infer<typeof resolveFeedbackSchema>;

export type FeedbackUser = z.infer<typeof feedbackUserSchema>;
export type FeedbackTypeSummary = z.infer<typeof feedbackTypeSummarySchema>;
export type FeedbackListItem = z.infer<typeof feedbackListItemSchema>;
export type PaginatedFeedbacks = z.infer<typeof paginatedFeedbacksSchema>;
